import React, {useState} from "react";
import {Check, ChevronDown, ChevronRight} from "lucide-react";
import {Box, Button, Chip, Stack, Typography} from "@mui/material";

const METHOD_COLORS = {
    GET: {bg: '#dbeafe', color: '#1d4ed8'},
    POST: {bg: '#dcfce7', color: '#15803d'},
    PUT: {bg: '#fef3c7', color: '#b45309'},
    PATCH: {bg: '#ede9fe', color: '#7c3aed'},
    DELETE: {bg: '#fee2e2', color: '#dc2626'},
};

const alternar = (setter, id) => setter((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
});

/*
 * Árvore módulo → funcionalidade → recurso com liberar/bloquear, a mesma do perfil da licença.
 * `modulos` vem do `buildPermissionTree`; funcionalidade sem recurso visível e módulo sem
 * funcionalidade visível ficam de fora. Quem usa guarda a seleção e responde aos cliques.
 */
export default function ArvoreDeRecursos({
    modulos,
    isLiberado,
    onAlternar,
    onDefinirTodos,
    somenteLeitura = false,
    maxHeight = 500,
}) {
    const [expandedModulos, setExpandedModulos] = useState(new Set());
    const [expandedFuncionalidades, setExpandedFuncionalidades] = useState(new Set());

    return (
        <Box sx={{border: '1px solid #e5e7eb', borderRadius: 2, maxHeight, overflowY: 'auto'}}>
            {modulos.map((modulo) => {
                const funcionalidadesVisiveis = modulo.funcionalidades.filter((item) => item.recursos.length > 0);
                if (!funcionalidadesVisiveis.length) return null;
                const recursosDoModulo = funcionalidadesVisiveis.flatMap((funcionalidade) => funcionalidade.recursos);

                return (
                    <Box key={modulo.id} sx={{borderBottom: '1px solid #e5e7eb', '&:last-child': {borderBottom: 0}}}>
                        <Box sx={{bgcolor: '#f9fafb', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                            <Button onClick={() => alternar(setExpandedModulos, modulo.id)} sx={{justifyContent: 'flex-start', color: '#111827', textTransform: 'none', flex: 1}}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {expandedModulos.has(modulo.id) ? <ChevronDown size={18} color="#4b5563" /> : <ChevronRight size={18} color="#4b5563" />}
                                    <Typography>{modulo.nome}</Typography>
                                </Stack>
                            </Button>
                            {!somenteLeitura ? (
                                <Stack direction="row" spacing={1}>
                                    <Button size="small" onClick={() => onDefinirTodos(recursosDoModulo, true)} sx={{bgcolor: '#f0fdf4', color: '#15803d', textTransform: 'none', '&:hover': {bgcolor: '#dcfce7'}}}>Liberar todos</Button>
                                    <Button size="small" onClick={() => onDefinirTodos(recursosDoModulo, false)} sx={{bgcolor: '#fef2f2', color: '#b91c1c', textTransform: 'none', '&:hover': {bgcolor: '#fee2e2'}}}>Bloquear todos</Button>
                                </Stack>
                            ) : null}
                        </Box>

                        {expandedModulos.has(modulo.id) ? funcionalidadesVisiveis.map((funcionalidade) => (
                            <Box key={funcionalidade.id} sx={{borderTop: '1px solid #f3f4f6'}}>
                                <Box sx={{p: 1.5, pl: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                                    <Button onClick={() => alternar(setExpandedFuncionalidades, funcionalidade.id)} sx={{justifyContent: 'flex-start', color: '#1f2937', textTransform: 'none', flex: 1}}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {expandedFuncionalidades.has(funcionalidade.id) ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                                            <Typography variant="body2">{funcionalidade.nome}</Typography>
                                        </Stack>
                                    </Button>
                                    {!somenteLeitura ? (
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" onClick={() => onDefinirTodos(funcionalidade.recursos, true)} sx={{bgcolor: '#f0fdf4', color: '#15803d', textTransform: 'none', '&:hover': {bgcolor: '#dcfce7'}}}>Liberar</Button>
                                            <Button size="small" onClick={() => onDefinirTodos(funcionalidade.recursos, false)} sx={{bgcolor: '#fef2f2', color: '#b91c1c', textTransform: 'none', '&:hover': {bgcolor: '#fee2e2'}}}>Bloquear</Button>
                                        </Stack>
                                    ) : null}
                                </Box>

                                {expandedFuncionalidades.has(funcionalidade.id) ? funcionalidade.recursos.map((recurso) => {
                                    const allowed = isLiberado(recurso.id);
                                    const methodStyle = METHOD_COLORS[recurso.httpMethod] || {bg: '#f3f4f6', color: '#374151'};
                                    return (
                                        <Box key={recurso.id} sx={{p: 1.5, pl: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, borderTop: '1px solid #f3f4f6', bgcolor: allowed ? 'rgba(220, 252, 231, 0.3)' : '#fff', '&:hover': {bgcolor: allowed ? 'rgba(220, 252, 231, 0.45)' : '#f9fafb'}}}>
                                            <Box sx={{flex: 1}}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5}}>
                                                    <Typography variant="body2" sx={{color: '#111827'}}>{recurso.resourceName}</Typography>
                                                    <Chip size="small" label={recurso.httpMethod} sx={{bgcolor: methodStyle.bg, color: methodStyle.color, fontWeight: 500, height: 22}} />
                                                </Stack>
                                                <Typography variant="caption" sx={{color: '#6b7280', fontFamily: 'monospace'}}>{recurso.endpoint}</Typography>
                                            </Box>
                                            <Button
                                                variant="text"
                                                onClick={() => onAlternar(recurso)}
                                                disabled={somenteLeitura}
                                                sx={{textTransform: 'none', color: allowed ? '#15803d' : '#374151'}}
                                                startIcon={allowed ? <Check size={16} /> : null}
                                            >
                                                {allowed ? 'Liberado' : 'Bloqueado'}
                                            </Button>
                                        </Box>
                                    );
                                }) : null}
                            </Box>
                        )) : null}
                    </Box>
                );
            })}
        </Box>
    );
}
